/**
 * A stored briefing finding as returned by lma-scheduler's GET /api/v1/briefings
 * (proxied through nginx as /briefing-api/briefings).
 *
 * One org can have SEVERAL findings on the same day — the row identity is
 * (orgKey, signalType, kpiId), not orgKey alone.
 */
export interface IBriefing {
  date: string;
  orgKey: string;
  severity: string; // WARN | INFO | GREEN
  headline: string;
  reason: string;
  recommendation: string;
  source: string; // AI | TEMPLATE
  model?: string;
  generatedAt?: string;
  /** Which signal produced the finding, e.g. KPI_TARGET_BREACH. */
  signalType?: string;
  /** The KPI the finding is about; empty when it is not about one specific KPI. */
  kpiId?: string;
  /** Ranking score the per-org cap selected on; higher is more important. */
  score?: number;
  /** Raw facts JSON the finding was built from ("Zahlen anzeigen"). */
  factsJson?: string;
}

/** German labels for the signal categories, for the category column and search. */
export const SIGNAL_LABELS: Record<string, string> = {
  KPI_TARGET_BREACH: 'Zielwert',
  KPI_DEVIATION: 'Abweichung',
  VOLUME_SPIKE: 'Menge hoch',
  VOLUME_DROP: 'Menge niedrig',
  FORECAST_ACCURACY: 'Prognosegüte',
  DATA_GAP: 'Datenlücke',
};

/**
 * Group headings, in reading order: what the base itself set as a goal first, then how it moved, then
 * volume, then the caveats. Findings of an unknown signal fall into "Weitere Hinweise".
 */
export const SIGNAL_GROUPS: Array<{ title: string; signals: string[] }> = [
  { title: 'Zielwerte', signals: ['KPI_TARGET_BREACH'] },
  { title: 'Auffällige Kennzahlen', signals: ['KPI_DEVIATION'] },
  { title: 'Mengen', signals: ['VOLUME_SPIKE', 'VOLUME_DROP', 'FORECAST_ACCURACY'] },
  { title: 'Datenqualität', signals: ['DATA_GAP'] },
];

export function signalLabel(signalType?: string): string {
  if (!signalType) {
    return '';
  }
  return SIGNAL_LABELS[signalType] || signalType;
}

export function groupTitle(signalType?: string): string {
  const group = SIGNAL_GROUPS.find((g) => signalType && g.signals.includes(signalType));
  return group ? group.title : 'Weitere Hinweise';
}

/** Stable row identity — an org can carry several findings on the same day. */
export function briefingKey(b: IBriefing): string {
  return `${b.orgKey}|${b.signalType || ''}|${b.kpiId || ''}`;
}

/** Attribution shape inside factsJson — the tour that drove a count-of-a-bad-thing finding. */
type Attribution = { childLabel?: string; driverLabel?: string; sharePercent?: number };
/** Peer-comparison shape inside factsJson — where the base stands against its Area. */
type PeerContext = { peerCount?: number; quartile?: number };

/**
 * Reads the attribution and peer context out of factsJson for the enrichment chips. The backend
 * already renders these into the reason text; the chips make them scannable without reading the prose.
 * Any parse problem yields no chips rather than an error.
 */
export function enrichmentChips(b: IBriefing): string[] {
  if (!b.factsJson) {
    return [];
  }
  const chips: string[] = [];
  try {
    const facts = JSON.parse(b.factsJson) as { attribution?: Attribution; peerContext?: PeerContext };
    const a = facts.attribution;
    if (a && a.sharePercent && a.childLabel) {
      chips.push(`${a.sharePercent}% aus ${a.childLabel}`);
    }
    const p = facts.peerContext;
    if (p && p.quartile === 1) {
      chips.push(`unteres Viertel der Area (${p.peerCount ?? '?'})`);
    } else if (p && p.quartile === 4) {
      chips.push(`oberes Viertel der Area (${p.peerCount ?? '?'})`);
    }
  } catch {
    /* malformed facts — no chips */
  }
  return chips;
}
